import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProfileModalProvider, useProfileModal } from './context/ProfileModalContext';
import { InventoryModalProvider, useInventoryModal } from './context/InventoryModalContext';
import { FeedbackProvider } from './context/FeedbackContext';
import { ShareProvider } from './context/ShareContext';
import { BackupModalProvider, useBackupModal } from './context/BackupModalContext';
import { HelpProvider } from './context/HelpContext';
import { DialogProvider } from './context/DialogContext';
import BackupRestoreModal from './components/BackupRestoreModal';
import ProfileModal from './components/ProfileModal';
import InventoryModal from './components/InventoryModal';
import FeedbackModal from './components/FeedbackModal';
import ShareModal from './components/ShareModal';
import HelpModal from './components/HelpModal';
import DialogManager from './components/DialogManager';
import HomePage from './pages/HomePage';
import SAAHomePage from './pages/SAAHomePage';
import WorkingStepsPage from './pages/WorkingStepsPage';
import Step1 from './pages/Step1';
import Step2 from './pages/Step2';
import Step3 from './pages/Step3';
import Step4 from './pages/Step4';
import Step5 from './pages/Step5';
import Step6 from './pages/Step6';
import Step7 from './pages/Step7';
import Step8 from './pages/Step8';
import Step9 from './pages/Step9';
import Step10 from './pages/Step10';
import Step11 from './pages/Step11';
import Step12 from './pages/Step12';
import Admin from './pages/Admin';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ExpiredLink from './components/ExpiredLink';
import ShareRedirect from './components/ShareRedirect';
import SelfAssessPage from './pages/SelfAssessPage';
import DiscoveryPage from './pages/DiscoveryPage';
import OnboardingWizard from './components/OnboardingWizard';
import AA from './pages/programs/AA';
import NA from './pages/programs/NA';
import OA from './pages/programs/OA';
import GA from './pages/programs/GA';
import DA from './pages/programs/DA';
import CoDA from './pages/programs/CoDA';
import CA from './pages/programs/CA';

import CustomLoginModal from './components/auth/CustomLoginModal';
import Footer from './components/Footer';

// Import UserKeyService for testing
import { UserKeyService } from './services/userKeyService';

// Wrapper component to provide auth context to routes
const AppRoutes: React.FC = () => {
  const { user, logout, forceClearAuth, refreshUserState } = useAuth();
  const { isProfileModalOpen, closeProfileModal } = useProfileModal();
  const { isInventoryModalOpen, closeInventoryModal, onInventoryUpdate, currentVersions, inventoryStatuses, initialTab } = useInventoryModal();
  const { isBackupModalOpen, closeBackupModal } = useBackupModal();
  const [isCustomLoginModalOpen, setIsCustomLoginModalOpen] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'signup'>('login');
  // Feedback modal is handled by the FeedbackModal component itself

  const handleCustomLogin = () => {
    setLoginModalMode('login');
    setIsCustomLoginModalOpen(true);
  };

  const handleCreateAccount = () => {
    setLoginModalMode('signup');
    setIsCustomLoginModalOpen(true);
  };

  const handleLoginSuccess = async (user: any) => {
    setIsCustomLoginModalOpen(false);
    
    // Refresh the user state in AuthContext to update the UI immediately
    try {
      await refreshUserState();
    } catch (error) {
      console.error('Error refreshing auth state:', error);
    }
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage user={user} onLogout={logout} onLogin={handleCustomLogin} onCreateAccount={handleCreateAccount} />} />
        <Route path="/index.html" element={<HomePage user={user} onLogout={logout} onLogin={handleCustomLogin} onCreateAccount={handleCreateAccount} />} />
        {/* SAA Program Routes - Simplified routing structure */}
        <Route path="/saa/12-steps" element={<Navigate to="/saa?tab=steps" replace />} />
        <Route path="/saa/12-steps/step-1" element={<Step1 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-2" element={<Step2 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-3" element={<Step3 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-4" element={<Step4 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-5" element={<Step5 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-6" element={<Step6 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-7" element={<Step7 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-8" element={<Step8 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-9" element={<Step9 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-10" element={<Step10 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-11" element={<Step11 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/saa/12-steps/step-12" element={<Step12 user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/admin" element={<Admin user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/expired-link" element={<ExpiredLink />} />
        <Route path="/s/:documentId" element={<ShareRedirect />} />
        <Route path="/self-assess" element={<SelfAssessPage user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/onboarding" element={<OnboardingWizard />} />
        <Route path="/discover" element={<DiscoveryPage user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        
        {/* Program Landing Pages */}
        <Route path="/saa" element={<SAAHomePage user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/aa" element={<AA user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/na" element={<NA user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/oa" element={<OA user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/ga" element={<GA user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/da" element={<DA user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/coda" element={<CoDA user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        <Route path="/ca" element={<CA user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
        
        {/* Catch-all route for any unmatched paths */}
        <Route path="*" element={<HomePage user={user} onLogout={logout} onLogin={handleCustomLogin} />} />
      </Routes>
      
      {/* Footer - appears on all pages */}
      <Footer />
      
      {/* Custom Authentication Modal */}
      <CustomLoginModal
        isOpen={isCustomLoginModalOpen}
        onClose={() => setIsCustomLoginModalOpen(false)}
        onSuccess={handleLoginSuccess}
        mode={loginModalMode}
      />
      
      <ProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={closeProfileModal} 
        user={user} 
      />
      
      <InventoryModal 
        isOpen={isInventoryModalOpen} 
        onClose={closeInventoryModal} 
        user={user}
        onInventoryUpdate={onInventoryUpdate}
        currentVersions={currentVersions}
        inventoryStatuses={inventoryStatuses}
        initialTab={initialTab}
      />
      
      {user && (
        <FeedbackModal 
          userEmail={user.email}
        />
      )}
      
      {user && (
        <ShareModal 
          user={user}
        />
      )}
      
      {user && (
        <BackupRestoreModal 
          isOpen={isBackupModalOpen}
          onClose={closeBackupModal}
        />
      )}
      
      <HelpModal />
      
      <DialogManager />

    </>
  );
};

function App() {
  // Test if UserKeyService is available
  // App component loaded

  
  return (
    <DialogProvider>
      <AuthProvider>
        <ProfileModalProvider>
          <InventoryModalProvider>
            <FeedbackProvider>
              <ShareProvider>
                <BackupModalProvider>
                  <HelpProvider>
                    <BrowserRouter>
                      <AppRoutes />
                    </BrowserRouter>
                  </HelpProvider>
                </BackupModalProvider>
              </ShareProvider>
            </FeedbackProvider>
          </InventoryModalProvider>
        </ProfileModalProvider>
      </AuthProvider>
    </DialogProvider>
  );
}

export default App;
